import Button from '@/shared/Button'
import FormAutocomplete from '@/shared/FormCreator/FormItem/FormAutocomplete'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

import HighlightOff from '@mui/icons-material/HighlightOff'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import debounce from '@mui/material/utils/debounce'

import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['userGroup/getAddableUsers'],
  isSubmitting: loading['userGroup/addUsersToUserGroup'],
})

const Selection = ({ data, onRemove }) => (
  <Chip
    icon={
      <IconButton
        size="small"
        sx={{ padding: 0 }}
        onClick={() => {
          onRemove(data?.id)
        }}
      >
        <HighlightOff />
      </IconButton>
    }
    label={data?.label}
  />
)

const AddUsersToUserGroupDialog = ({
  dispatch,
  isLoading,
  isSubmitting,
  onClose,
  onComplete,
  data,
  ...props
}) => {
  const { groupId, groupName } = data || {}
  const { t } = useTranslation()

  const searchInputRef = useRef()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState({})
  const [selected, setSelected] = useState([])
  const [selectedUser, setSelectedUser] = useState()
  const [page, setPage] = useState(0)

  const { data: searchResultsData = [], paging } = searchResults

  const handleClose = () => {
    setSelected([])
    setSearchInput('')
    setSearchResults({})
    setSelectedUser()
    setPage(0)
    onClose?.()
  }

  const handleSubmit = () => {
    const postData = selected.map(s => s.id)

    dispatch({
      type: 'userGroup/addUsersToUserGroup',
      payload: { userList: postData, userGroupId: groupId },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: {
            message: groupName
              ? t('messages.users_added_group_groupname', { name: groupName })
              : t('messages.users_added_group'),
            severity: 'success',
          },
        })

        onComplete?.()
        handleClose?.()
      },
    })
  }

  const handleSelectionRemove = id => {
    setSelected(selected.filter(s => s.id !== id))
  }

  const handleAddUser = () => {
    setSelected([selectedUser, ...selected])
  }

  const fetchData = useCallback(() => {
    if (groupId && searchInputRef.current) {
      dispatch({
        type: 'userGroup/getAddableUsers',
        payload: { groupId, pageIndex: page, pageSize: 100, searchEmail: searchInputRef.current },
        successCallback: response => {
          const { data: responseData, paging: responsePaging } = response
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ id: d.userId, label: d.email }))

          setSearchResults(s => ({
            data: pageIndex > 0 ? [...(s.data || []), ...newData] : newData,
            paging: responsePaging,
          }))
        },
      })
    } else {
      setSearchResults({})
    }
  }, [dispatch, groupId, page])

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  const fetchDataDebounce = useMemo(
    () =>
      debounce(() => {
        fetchData()
      }, 400),
    [fetchData],
  )

  useEffect(() => {
    fetchDataDebounce()
  }, [searchInput, page, fetchDataDebounce])

  return (
    <Dialog {...props} open={!!data} maxWidth={'md'}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme => theme.palette.primary.main,
        }}
      >
        {t('general.add_registered_user')}
      </DialogTitle>
      <DialogContent sx={{ width: 500 }}>
        <Typography mb={2} fontWeight={'bold'} sx={{ display: 'flex' }}>
          {`${t('general.user_group_name')}:`}
          <Typography ml={1} color={'primary'} fontWeight={'bold'}>
            {groupName}
          </Typography>
        </Typography>
        <Stack mb={10} direction="row" justifyContent="flex-start">
          <Box>
            <FormAutocomplete
              inputConfig={{
                onChange: value => {
                  setSelectedUser(value)
                },
                onInputChange: (_, value, reason) => {
                  searchInputRef.current = value

                  if (['input', 'clear'].includes(reason)) {
                    setPage(0)
                    setSearchInput(value)
                  }
                },
                onLoadMore: () => {
                  const { hasNext } = paging

                  if (!isLoading && hasNext) {
                    loadMoreDebounce()
                  }
                },
              }}
              options={searchResultsData}
              filterOptions={opts => opts}
              getOptionDisabled={option => selected.some(s => s.id === option.id)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(renderProps, option) => (
                <Box component="li" {...renderProps} key={option.id}>
                  {option.label}
                </Box>
              )}
            />
          </Box>
          <Button
            sx={{ ml: 2 }}
            variant="outlined"
            disabled={!selectedUser || selected.some(s => s.id === selectedUser.id)}
            onClick={() => {
              handleAddUser()
            }}
          >
            {t('general.add')}
          </Button>
        </Stack>

        <Card variant="outlined">
          <CardContent sx={{ minHeight: 100 }}>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {selected.map(obj => (
                <Selection key={obj?.id} data={obj} onRemove={handleSelectionRemove} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ alignSelf: 'center' }}>
        <Button
          sx={{ minWidth: 150, marginRight: '30px' }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleClose}
        >
          {t('general.cancel')}
        </Button>
        <Button
          sx={{ minWidth: 150 }}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(AddUsersToUserGroupDialog)
